import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  applyDocumentDirection,
  isAppLocale,
  type AppLocale,
} from '@/i18n'
import { cn } from '@/lib/cn'

export const LOCALE_FLAGS: Record<AppLocale, string> = {
  en: '🇺🇸',
  tr: '🇹🇷',
  de: '🇩🇪',
  ar: '🇸🇦',
  zh: '🇨🇳',
}

const LOCALE_COUNTRY: Record<AppLocale, string> = {
  en: 'United States',
  tr: 'Türkiye',
  de: 'Deutschland',
  ar: 'السعودية',
  zh: '中国',
}

/**
 * Compact language button → searchable locale modal (portaled to body).
 * Must portal: header uses backdrop-filter which traps `position: fixed`.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const current = isAppLocale(i18n.language) ? i18n.language : 'en'

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex h-7 items-center gap-1.5 rounded-sm border border-terminal-border/50',
          'bg-terminal-surface px-2 text-[11px] font-semibold text-terminal-fg',
          'hover:bg-terminal-elevated focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          className,
        )}
        aria-label={t('language')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="text-sm leading-none" aria-hidden>
          {LOCALE_FLAGS[current]}
        </span>
        <span className="max-w-[5.5rem] truncate">{LOCALE_LABELS[current]}</span>
        <ChevronDown />
      </button>

      <LanguageModal
        open={open}
        current={current}
        onClose={() => setOpen(false)}
        onSelect={async (locale) => {
          await i18n.changeLanguage(locale)
          applyDocumentDirection(locale)
          setOpen(false)
        }}
      />
    </>
  )
}

function LanguageModal({
  open,
  current,
  onClose,
  onSelect,
}: {
  open: boolean
  current: AppLocale
  onClose: () => void
  onSelect: (locale: AppLocale) => void | Promise<void>
}) {
  const { t } = useTranslation('common')
  const titleId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    const prev = document.activeElement as HTMLElement | null
    const id = window.setTimeout(() => inputRef.current?.focus(), 40)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.clearTimeout(id)
      window.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = prevOverflow
      prev?.focus?.()
    }
  }, [open, onClose])

  const locales = useMemo(() => {
    const q = query.trim().toLowerCase()
    return SUPPORTED_LOCALES.filter((locale) => {
      if (!q) return true
      const hay = [
        LOCALE_LABELS[locale],
        LOCALE_COUNTRY[locale],
        locale,
        locale.toUpperCase(),
      ]
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [query])

  if (!open || !mounted) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label={t('actions.cancel')}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative flex w-full max-w-md flex-col',
          'rounded-t-xl border border-terminal-border bg-terminal-surface shadow-2xl',
          'sm:rounded border',
          'animate-[modal-in_180ms_ease-out]',
          'max-h-[min(28rem,90dvh)]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-terminal-border/40 px-4 pb-3 pt-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                id={titleId}
                className="text-base font-semibold text-terminal-fg"
              >
                {t('languageModal.title')}
              </h2>
              <p className="mt-0.5 text-[11px] text-terminal-muted">
                {t('languageModal.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-terminal-muted hover:bg-terminal-elevated hover:text-terminal-fg"
              aria-label={t('actions.close')}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-terminal-muted">
              <SearchIcon />
            </span>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('languageModal.search')}
              className={cn(
                'h-10 w-full rounded border border-terminal-border/50 bg-terminal-bg',
                'ps-9 pe-3 text-sm text-terminal-fg placeholder:text-terminal-muted',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
              )}
            />
          </div>
        </div>

        <ul className="terminal-scroll min-h-0 flex-1 overflow-y-auto p-2">
          {locales.map((locale) => {
            const selected = locale === current
            return (
              <li key={locale}>
                <button
                  type="button"
                  onClick={() => void onSelect(locale)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-start transition-colors',
                    selected
                      ? 'bg-accent/12 ring-1 ring-accent/30'
                      : 'hover:bg-terminal-elevated/80',
                  )}
                >
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-terminal-elevated text-xl"
                    aria-hidden
                  >
                    {LOCALE_FLAGS[locale]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-terminal-fg">
                      {LOCALE_LABELS[locale]}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-terminal-muted">
                      {LOCALE_COUNTRY[locale]} · {locale.toUpperCase()}
                    </span>
                  </span>
                  {selected ? (
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-white">
                      <CheckIcon />
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
          {locales.length === 0 ? (
            <li className="px-3 py-10 text-center text-xs text-terminal-muted">
              {t('actions.noResults')}
            </li>
          ) : null}
        </ul>
      </div>
    </div>,
    document.body,
  )
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3 opacity-60" aria-hidden>
      <path
        d="M3 4.5L6 7.5L9 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 10.5L14 14"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden>
      <path
        d="M2.5 6.5L5 9l4.5-5.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
